import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ImportMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (rows: any[], onProgress?: (progress: { current: number; total: number; percentage: number }) => void) => Promise<void>;
  isLoading?: boolean;
}

const ImportMembersModal: React.FC<ImportMembersModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isLoading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number; percentage: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset progress when modal closes
  const handleClose = () => {
    setImportProgress(null);
    setError(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!isOpen) return null;

  const downloadTemplate = () => {
    const headers = ['full_name', 'national_id', 'phone', 'password', 'role'];
    const example = ['John Doe', 'NID123456', '+6781234567', 'password123', 'member'];
    const csvContent = [headers.join(','), example.join(',')].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'member_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a valid CSV file.');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to import.');
      return;
    }

    setError(null);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim() !== '');
        if (lines.length < 2) {
          setError('The CSV file is empty or missing data rows.');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const expectedHeaders = ['full_name', 'national_id', 'phone', 'password', 'role'];
        
        // Basic header validation
        const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          setError(`Missing required columns: ${missingHeaders.join(', ')}`);
          return;
        }

        const rows = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i];
          });
          row.lineNumber = index + 2;
          return row;
        });

        // Basic row validation
        const invalidRows = rows.filter(r => !r.full_name || !r.national_id);
        if (invalidRows.length > 0) {
          setError(`Row(s) ${invalidRows.map(r => r.lineNumber).join(', ')} are missing Full Name or National ID.`);
          return;
        }

        // Reset progress
        setImportProgress({ current: 0, total: rows.length, percentage: 0 });
        setError(null);

        try {
          await onImport(rows, (progress) => {
            setImportProgress(progress);
          });
          
          // Keep modal open to show results, don't auto-close
          // User can close manually after reviewing results
        } catch (err: any) {
          console.error('Import error:', err);
          setError(err.message || 'Import failed. Please try again.');
        } finally {
          // Reset file input after import completes
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      } catch (err: any) {
        console.error('Error parsing CSV:', err);
        setError('Failed to parse CSV file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-md shadow-elevation-2 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Upload" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Import Members</h3>
            <p className="text-sm text-muted-foreground">
              Bulk import members using a CSV template.
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {importProgress && isLoading && (
          <div className="mb-4 p-4 bg-muted/30 rounded-md border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                Processing import...
              </span>
              <span className="text-sm text-muted-foreground">
                {importProgress.current} / {importProgress.total} ({importProgress.percentage}%)
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
              <div 
                className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${importProgress.percentage}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {importProgress.current === importProgress.total 
                ? 'Finalizing import...' 
                : `Importing member ${importProgress.current} of ${importProgress.total}...`}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3 max-h-64 overflow-y-auto">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-destructive font-medium whitespace-pre-wrap">{error}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {!isLoading && importProgress && importProgress.current === importProgress.total && !error && (
          <div className="mb-4 p-4 bg-success/10 border border-success/20 rounded-md flex items-start gap-3">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-success font-medium">
                ✅ Import completed successfully! All {importProgress.total} member(s) have been imported.
              </p>
            </div>
          </div>
        )}

        {/* Completion Message */}
        {!isLoading && importProgress && importProgress.current === importProgress.total && (
          <div className="mb-4 p-3 bg-muted/30 rounded-md border border-border">
            <p className="text-xs text-muted-foreground text-center">
              Import completed. You can close this window or import another file.
            </p>
          </div>
        )}

        <div className="space-y-6">
          <div className="p-4 bg-muted/30 rounded-md border border-border">
            <h4 className="text-sm font-semibold text-foreground mb-2">Step 1: Download Template</h4>
            <p className="text-xs text-muted-foreground mb-4">
              Use our standard format to ensure your data is imported correctly.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={downloadTemplate}
              className="w-full"
            >
              Download CSV Template
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-muted/30 rounded-md border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-2">Step 2: Upload Completed File</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Select your edited CSV file to start the import.
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full text-xs text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                disabled={isLoading}
                ref={fileInputRef}
              />
              {file && (
                <p className="mt-2 text-xs text-success flex items-center gap-1">
                  <Icon name="CheckCircle" size={12} />
                  {file.name} selected
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                {importProgress && importProgress.current === importProgress.total ? 'Close' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                disabled={!file || isLoading}
                iconName="Upload"
                iconPosition="right"
              >
                {isLoading ? 'Importing...' : 'Start Import'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImportMembersModal;
